import React, { Fragment, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@apollo/react-hooks';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers';
import { useTranslation } from 'react-i18next';

import { Button, FormErrors, Modal } from '@squirrellearningltd/go-teacher-components';

import { CHANGE_PASSWORD_MUTATION } from '../services/graphql';
import ForgotPassword from '../../Login/scenes/ForgotPassword';
import { Check } from 'react-feather';

const ChangePassword = ({ closeModal }) => {
    const [modalForgotPass, setModalForgotPass] = useState(false);
    const [formErrors, setFormErrors] = useState([]);
    const [success, setSuccess] = useState(false);
    const { t } = useTranslation();

    const [changePassword, { loading }] = useMutation(CHANGE_PASSWORD_MUTATION, {
        onCompleted: data => {
            if (data.changePassword.errors && data.changePassword.errors.length !== 0) {
                setFormErrors(data.changePassword.errors);
                return;
            }

            if (data.changePassword.user.id) {
                setSuccess(true);
                reset({
                    existingPassword: '',
                    newPassword: '',
                    passwordConfirmation: ''
                })
            }
        }
    });

    const schema = Yup.object().shape({
        existingPassword: Yup.string()
            .required(t('req_enter_current_password')),
        newPassword: Yup.string()
            .required(t('req_enter_new_password')),
        passwordConfirmation: Yup.string()
            .oneOf([Yup.ref('newPassword'), null], t('req_pw_must_match'))
    });

    const { register, handleSubmit, errors, reset } = useForm({
        resolver: yupResolver(schema),
        mode: 'onBlur'
    });

    const onSubmit = values => {
        setFormErrors([]);
        const { existingPassword, newPassword, passwordConfirmation } = values;
        changePassword({
            variables: {
                existingPassword,
                newPassword,
                passwordConfirmation
            }
        })
    };

    return (
        <Fragment>
            <form onSubmit={handleSubmit(onSubmit)}>
                <h1 className='light u-m-base-3'>{t('please_set_new_pw')}</h1>
                <div className='basic-form__group'>
                    <label htmlFor='existingPassword' className='basic-form__text-label'>{t('current_password')}</label>
                    <input
                        name='existingPassword'
                        id='existingPassword'
                        className='basic-form__text-box'
                        type='password'
                        ref={register}
                    />
                </div>
                {errors.existingPassword && <p className='basic-form__hint'>{errors.existingPassword.message}</p>}
                <div className='basic-form__group'>
                    <label htmlFor='newPassword' className='basic-form__text-label'>{t('new_password')}</label>
                    <input
                        name='newPassword'
                        id='newPassword'
                        className='basic-form__text-box'
                        type='password'
                        ref={register}
                    />
                </div>
                {errors.newPassword && <p className='basic-form__hint'>{errors.newPassword.message}</p>}
                <div className='basic-form__group'>
                    <label htmlFor='passwordConfirmation' className='basic-form__text-label'>{t('confirm_new_password')}</label>
                    <input
                        name='passwordConfirmation'
                        id='passwordConfirmation'
                        className='basic-form__text-box'
                        type='password'
                        ref={register}
                    />
                </div>
                {errors.passwordConfirmation && <p className='basic-form__hint'>{errors.passwordConfirmation.message}</p>}
                <Button type='button' outline onClick={() => setModalForgotPass(true)}>{t('forgotten_password')}</Button>
                <div className='u-m-top-3'>
                    <Button className='u-m-right-1' type='submit'>{loading ? t('saving') : t('button.save')}</Button>
                    <Button type='button' outline onClick={() => closeModal()}>{t('button.cancel')}</Button>
                </div>
            </form>
            {formErrors.length !== 0 && <FormErrors errors={formErrors} />}
            {
                success && (
                    <p className='basic-form__success'><Check className='basic-form__success__icon'/>{t('new_password_saved')}</p>
                )
            }
            {
                modalForgotPass && (
                    <Modal closeModal={() => setModalForgotPass(false)}>
                        <ForgotPassword closeModal={() => setModalForgotPass(false)} />
                    </Modal>
                )
            }
        </Fragment>
    )
}

export default ChangePassword;
